<script setup>
import { useStore } from "@/stores";
import { defineProps, computed, ref, watch } from "vue";
import draggable from "vuedraggable";

const props = defineProps({
  day: {
    type: Object,
    required: true,
  },
  dayIndex: {
    type: Number,
    required: true,
  },
  weekId: {
    type: String,
    required: true,
  },
});

const store = useStore().rows;

const rows = computed({
  get: () => {
    // Ensure we are accessing the store state safely
    if (
      store.weeks[props.weekId] &&
      store.weeks[props.weekId][props.dayIndex]
    ) {
      return store.weeks[props.weekId][props.dayIndex].rows;
    }
    return [];
  },
  set: (value) => {
    if (
      store.weeks[props.weekId] &&
      store.weeks[props.weekId][props.dayIndex]
    ) {
      store.weeks[props.weekId][props.dayIndex].rows = value;
    }
  },
});
const getAutoUrl = (ticket) => {
  if (!ticket) return "";
  if (!ticket.trim().match(/^(ADR|SGSD)-\S+$/i)) return "";
  return `https://studiogenesis.atlassian.net/browse/${ticket.trim().toUpperCase()}`;
};

const modalOpen = ref(false);
const modalRow = ref(null);
const modalText = ref("");
const modalUrl = ref("");

const openModal = (row) => {
  modalRow.value = row;
  modalText.value = row.ticket || "";
  modalUrl.value = row.ticketUrl || getAutoUrl(row.ticket);
  modalOpen.value = true;
};

const saveModal = () => {
  if (!modalRow.value) return;
  modalRow.value.ticket = modalText.value;
  modalRow.value.ticketUrl = modalUrl.value;
  modalOpen.value = false;
};

const closeModal = () => {
  modalOpen.value = false;
};

watch(modalText, (val) => {
  const auto = getAutoUrl(val);
  if (auto) modalUrl.value = auto;
});

const totalTemps = computed(() => {
  let totalMinutes = 0;
  for (const row of rows.value) {
    if (!row.temps) continue;
    const hMatch = row.temps.match(/(\d+)h/);
    const mMatch = row.temps.match(/(\d+)m/);
    if (hMatch) totalMinutes += parseInt(hMatch[1]) * 60;
    if (mMatch) totalMinutes += parseInt(mMatch[1]);
  }
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  let result = "";
  if (hours > 0) result += `${hours}h`;
  if (minutes > 0) {
    if (result) result += " ";
    result += `${minutes}m`;
  }
  return result || "0m";
});

const calculateDuration = (row) => {
  if (!row.entrada || !row.final) return;

  const [startH, startM] = row.entrada.split(":").map(Number);
  const [endH, endM] = row.final.split(":").map(Number);

  let diffMinutes = endH * 60 + endM - (startH * 60 + startM);

  if (diffMinutes < 0) {
    diffMinutes += 24 * 60;
  }

  const hours = Math.floor(diffMinutes / 60);
  const minutes = diffMinutes % 60;

  let durationString = "";
  if (hours > 0) {
    durationString += `${hours}h`;
  }
  if (minutes > 0) {
    if (durationString) durationString += " ";
    durationString += `${minutes}m`;
  }

  if (!durationString) {
    if (diffMinutes === 0) durationString = "0m";
  }

  row.temps = durationString;
};
</script>

<template>
  <div
    class="bg-white dark:bg-gray-800 rounded-lg shadow-md overflow-hidden border border-gray-200 dark:border-gray-700"
  >
    <div
      class="bg-gray-100 dark:bg-gray-900 px-4 py-3 border-b border-gray-200 dark:border-gray-700"
    >
      <h2 class="text-xl font-semibold text-gray-700 dark:text-gray-200">
        {{ day.dayName }} {{ day.date }}
      </h2>
    </div>
    <div class="p-4">
      <table class="w-full text-sm text-left text-gray-500 dark:text-gray-400">
        <thead
          class="text-xs text-gray-700 uppercase bg-gray-50 dark:bg-gray-700 dark:text-gray-400"
        >
          <tr>
            <th scope="col" class="px-3 py-2 w-8"></th>
            <th scope="col" class="px-3 py-2">SITE</th>
            <th scope="col" class="px-3 py-2">TICKET</th>
            <th scope="col" class="px-3 py-2">HORA ENTRADA</th>
            <th scope="col" class="px-3 py-2">HORA FINAL</th>
            <th scope="col" class="px-3 py-2">TEMPS</th>
            <th scope="col" class="px-3 py-2">INPTUTAT</th>
            <th scope="col" class="px-3 py-2"></th>
          </tr>
        </thead>
        <draggable
          v-model="rows"
          tag="tbody"
          item-key="id"
          handle=".cursor-move"
          ghost-class="bg-gray-100"
        >
          <template #item="{ element: row, index }">
            <tr
              class="bg-white border-b dark:bg-gray-800 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600"
            >
              <td class="px-3 py-2 text-center align-middle">
                <div class="cursor-move text-gray-400 hover:text-gray-600">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    class="h-6 w-6"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M4 8h16M4 16h16"
                    />
                  </svg>
                </div>
              </td>
              <td class="px-3 py-2">
                <input
                  v-model="row.site"
                  type="text"
                  class="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500"
                  placeholder="Site"
                />
              </td>
              <td class="px-3 py-2">
                <div class="flex items-center gap-1">
                  <a
                    v-if="row.ticketUrl"
                    :href="row.ticketUrl"
                    target="_blank"
                    rel="noopener noreferrer"
                    class="bg-gray-50 border border-gray-300 text-blue-600 no-underline text-sm rounded-lg block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 truncate text-center"
                  >
                    {{ row.ticket || row.ticketUrl }}
                  </a>
                  <input
                    v-else
                    v-model="row.ticket"
                    type="text"
                    class="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500"
                    placeholder="Ticket"
                  />
                  <button
                    @click="openModal(row)"
                    class="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 flex-shrink-0"
                    title="Editar ticket"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15.232 5.232l3.536 3.536M9 13l6.586-6.586a2 2 0 112.828 2.828L11.828 15.828a2 2 0 01-1.414.586H9v-2a2 2 0 01.586-1.414z" />
                    </svg>
                  </button>
                </div>
              </td>
              <td class="px-3 py-2">
                <input
                  v-model="row.entrada"
                  @change="calculateDuration(row)"
                  type="time"
                  class="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500"
                />
              </td>
              <td class="px-3 py-2">
                <input
                  v-model="row.final"
                  @change="calculateDuration(row)"
                  type="time"
                  class="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500"
                />
              </td>
              <td class="px-3 py-2">
                <input
                  v-model="row.temps"
                  type="text"
                  class="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500"
                  placeholder="Temps"
                />
              </td>
              <td class="px-3 py-2 text-center align-middle">
                <label class="relative inline-flex items-center cursor-pointer">
                  <input
                    v-model="row.inputat"
                    type="checkbox"
                    class="sr-only peer"
                  />
                  <div
                    class="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 dark:peer-focus:ring-blue-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-blue-600"
                  ></div>
                </label>
              </td>
              <td class="px-3 py-2 text-center">
                <button
                  @click="store.deleteRow(weekId, dayIndex, index)"
                  class="text-red-600 hover:text-red-900 dark:text-red-500 dark:hover:text-red-400"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    class="h-5 w-5"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                    />
                  </svg>
                </button>
              </td>
            </tr>
          </template>
        </draggable>
        <tfoot v-if="rows.length > 0">
          <tr
            class="bg-gray-50 dark:bg-gray-700 font-semibold text-gray-700 dark:text-gray-200"
          >
            <td colspan="5" class="px-3 py-2 text-right"></td>
            <td class="ps-6 py-2">{{ totalTemps }}</td>
            <td colspan="2"></td>
          </tr>
        </tfoot>
      </table>
      <div class="mt-4 flex justify-end">
        <button
          @click="store.addRow(weekId, dayIndex)"
          class="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 dark:bg-blue-500 dark:hover:bg-blue-600"
        >
          +
        </button>
      </div>
    </div>
  </div>

  <!-- Modal ticket -->
  <Teleport to="body">
    <div
      v-if="modalOpen"
      class="fixed inset-0 z-50 flex items-center justify-center"
      @click.self="closeModal"
    >
      <div class="absolute inset-0 bg-black/40" @click="closeModal" />
      <div class="relative bg-white dark:bg-gray-800 rounded-xl shadow-xl p-6 w-80 flex flex-col gap-4">
        <h3 class="text-sm font-semibold text-gray-700 dark:text-gray-200">Editar ticket</h3>
        <div class="flex flex-col gap-1">
          <label class="text-xs text-gray-500 dark:text-gray-400">Texto</label>
          <input
            v-model="modalText"
            type="text"
            class="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
            placeholder="ADR-123"
          />
        </div>
        <div class="flex flex-col gap-1">
          <label class="text-xs text-gray-500 dark:text-gray-400">Enlace</label>
          <input
            v-model="modalUrl"
            type="url"
            class="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
            placeholder="https://..."
          />
        </div>
        <div class="flex justify-end gap-2">
          <button
            @click="closeModal"
            class="px-3 py-1.5 text-sm text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white"
          >
            Cancelar
          </button>
          <button
            @click="saveModal"
            class="px-4 py-1.5 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700"
          >
            Guardar
          </button>
        </div>
      </div>
    </div>
  </Teleport>
</template>
